import {
  BlockReferenceContents,
  IGetBlockReferenceByIdQuery,
  OverrideSQuery,
} from '@rhizome/pgtypes';
import { DBToSlate } from '@rhizome/shared-slate-utils';
import { ExpandLess, ExpandMore, Link } from '@styled-icons/material';
import { MentionElementProps } from '@udecode/plate-ui-mention';
import { useRouter } from 'next/router';
import { useMemo, useState } from 'react';
import { useSelected } from 'slate-react';
import styled from 'styled-components';
import { DarkenMedium, useIsMobile } from '../../../styles/themes';
import { useFetcher } from '../../../utils/DataFetchers';
import { usePrivacy } from '../../../utils/usePrivacy';
import { ReadOnlyDisplay } from '../usedPlugins';
import { RenderBlockPreview } from './RenderPreview';
import { LazyTippy } from './TagMentionSlateComponent';

export const CreateBlockReferenceSlateComponent = (allowPreview: boolean) => {
  return function BlockReferenceSlateComponent({
    attributes,
    element,
    prefix,
    children,
  }: MentionElementProps & {
    element: BlockReferenceContents & { children: any[] };
  }) {
    const selected = useSelected();
    const router = useRouter();
    const privacy = usePrivacy();
    const isMobile = useIsMobile();

    const { data: block, status: blockStatus } = useFetcher<
      OverrideSQuery<IGetBlockReferenceByIdQuery>
    >(
      [
        'GetBlockReferenceById',
        {
          block_id: element?.block_id,
        },
      ],
      {}
    );
    const initialValue = useMemo(() => {
      if (block) {
        const converted = DBToSlate(block);
        return converted;
      }
      return [];
    }, [block]);

    // TODO: this is temporary, build this into the node data eventually
    const [open, setOpen] = useState(false);

    const PreviewRenderer = useMemo(() => {
      return RenderBlockPreview(element.block_id);
    }, [element.block_id]);

    const privateLink =
      '/notes/editor/' + element?.slate_parent + '#' + element?.block_id;

    const publicLink =
      '/post/' + element?.slate_parent + '/' + '?excerpt=' + element?.block_id;

    const title = block?.[0]?.parent_title || 'untitled';
    const mainComponent = (
      <BlockReference selected={selected}>
        <span>
          <LinkToBlock
            open={open}
            onClick={() => {
              if (privacy === 'private') {
                router.push(privateLink);
              } else {
                router.push(publicLink);
              }
            }}
          >
            {open ? null : (
              <>
                <Link
                  style={{
                    verticalAlign: 'medium',
                    color: '#363636d1',
                    height: '0.7em',
                  }}
                />{' '}
                to{' '}
              </>
            )}
            block from {'"' + title + '"'}
          </LinkToBlock>

          <DropDownButton
            onClick={() => {
              setOpen(!open);
            }}
          >
            {open ? (
              <ExpandLess
                style={{
                  verticalAlign: 'medium',
                  color: '#363636d1',
                  height: '1rem',
                }}
              />
            ) : (
              <ExpandMore
                style={{
                  verticalAlign: 'medium',
                  color: '#363636d1',
                  height: '0.7em',
                }}
              />
            )}
          </DropDownButton>
        </span>
        {open ? (
          <ContentWrapper contentEditable={false}>
            <FadeContainer>
              {block && block.length > 0 ? (
                <ReadOnlyDisplay nodes={initialValue} isRoot={false} />
              ) : (
                <h2>This block has been deleted</h2>
              )}
            </FadeContainer>
          </ContentWrapper>
        ) : null}
        {prefix}
        {children}
      </BlockReference>
    );
    return (
      <span contentEditable={false} {...(attributes || {})}>
        {isMobile || !allowPreview || open ? (
          mainComponent
        ) : (
          <LazyTippy
            render={PreviewRenderer}
            offset={[0, 0]}
            placement="right-start"
            arrow={false}
            interactive={true}
          >
            {mainComponent}
          </LazyTippy>
        )}
      </span>
    );
  };
};

const BlockReference = styled.span<{ selected: boolean }>`
  display: inline-block;
  line-height: 1;
  vertical-align: text-top;
  color: #363636d1;
  text-decoration: none !important;
  background-color: #6e6e6e4b;
  border-radius: 0.2em;
  outline: ${(props) => (props.selected ? `2px solid #363636d1` : 'none')};
  cursor: default;
  /* border: 1px solid #3636367f; */
  :hover {
    outline: ${(props) => `1px solid #363636d1`};
  }
  font-size: 1.05em;
`;

const DropDownButton = styled.div`
  display: inline-block;
  :hover {
    background-color: ${DarkenMedium};
  }
  float: right;
  padding: 0.1em 0.2em 0.1em 0.2em;
`;

const LinkToBlock = styled.div<{ open: boolean }>`
  display: inline-block;
  :hover {
    ${(props: { open: boolean }) => {
      if (props.open) {
        return `text-decoration: underline;`;
      } else {
        return `background-color: ${DarkenMedium};`;
      }
    }}
  }
  padding: 0.1em 0.2em 0.1em 0.2em;
`;

const FadeContainer = styled.span`
  display: inline-block;
  animation-name: fadein;
  animation-duration: 0.5s;
`;

const ContentWrapper = styled.span`
  display: block;
  max-height: 200px;
  width: 300px;
  padding: 10px;
  overflow-y: auto;
`;
